import { Res, TicketDTO } from 'core/api';
import { SvgIcon } from 'core/ui-next';
import React from 'react';
import { Box, Button, HStack, Span, StackProps, VStack } from '@chakra-ui/react';
import * as UI from 'core/ui';
import { Command } from 'pos/common';

const asPercentage = UI.Format.asPercentage;
const asMoney = UI.Format.asMoney;

const onShowPayments = async () => {
	const result = await Command.execute('showTicketPayments');

	if (!result.ok)
		return;

};

function Prepayments({ ticket }: { ticket: TicketDTO }) {

	if (ticket.PrepaidAmount === 0)
		return null;

	if (ticket.PrepaidAmount !== 0) {
		const isPending = ticket.PendingAmount >= 0;
		const boxProps: StackProps = {
			bg: isPending ? {
				base: 'red.100',
				_dark: 'red.300'
			} : {
				base: 'yellow.100',
				_dark: 'yellow.600'
			},
			width: 'full',
			borderRadius: 'sm',
			paddingY: '1',
			paddingX: '2',
			fontSize: 'sm',
			fontWeight: 'semibold',
			onClick: () => onShowPayments(),
			cursor: 'pointer',
			justifyContent: 'space-between'
		};
		const label = isPending
			? Res.Web.PREPAYMENT_PENDING_AMOUNT_CONTROL_LBL()
			: Res.Web.PREPAYMENT_WARNING_CONTROL_LBL();

		return <HStack {...boxProps}>
			<Box>{label}</Box>
			<Box>{asMoney(Math.abs(ticket.PendingAmount))}</Box>
		</HStack>;
	}
	return null;
}

function Discounts({ ticket }: { ticket: TicketDTO }) {
	if (ticket.DiscountRate === 0 && ticket.CashDiscount === 0)
		return null;

	const discountPercentage = ticket.DiscountPercentage !== 0 ? <Span>{`-${asPercentage(ticket.DiscountPercentage)} (${asMoney(ticket.DiscountPercentageInCash)})`}</Span> : null;
	const cashDiscount = ticket.CashDiscount !== 0 ? <Span>{`-${asMoney(ticket.CashDiscount)}`}</Span> : null;

	return <VStack w='full' fontSize={'sm'} fontWeight={'normal'} paddingX={'2'}>
		{discountPercentage &&
			<Box justifyContent={'space-between'} w='full' display='flex'>
				<Span>{Res.Web.WEB_KEYPAD_DISPLAY_TICKET_CONTROL_DISCOUNT_BTN()}</Span>
				{discountPercentage}
			</Box>
		}
		{cashDiscount &&
			<Box justifyContent={'space-between'} w='full' display='flex'>
				<Span>{Res.Web.WEB_KEYPAD_DISPLAY_TICKET_CONTROL_DISCOUNT_BTN()}</Span>
				{cashDiscount}
			</Box>
		}
	</VStack>;
}

function Total({ ticket }: { ticket: TicketDTO }) {
	return <Box justifyContent={'space-between'} w='full' display='flex' fontSize={'2xl'} fontWeight={'bold'}>
		<span>{Res.Web.TICKET_CONTROL_TOTAL()}</span>
		<span>{asMoney(ticket.NetAmount)}</span>
	</Box>;
}

type Props = { onToggleKeyboard?: () => void, ticket: TicketDTO, keypadIcon?: SvgIcon, isExpandedTicket?: boolean };

export function TicketTotal({ ticket, onToggleKeyboard, keypadIcon, isExpandedTicket }: Props): React.ReactNode {

	const expandedStyles = {
		'collapsed': {
			'bg': { base: '{colors.neutral.500}', _dark: '{colors.teal.800}' },
			'_hover': { bg: { base: '{colors.neutral.600}', _dark: '{colors.teal.600}' } },
			'_active': { bg: { base: '{colors.neutral.400} !important', _dark: '{colors.teal.900} !important' } },
			'border': '2px solid',
			'borderColor': { base: '{colors.neutral.800}', _dark: '{colors.neutral.300}' },
		},
		'expanded': {
			'bg': 'secondary',
			'_hover': { bg: 'secondary._hover' },
			'_active': { bg: 'secondary._active !important' },
			'border': 'none',
			'borderColor': 'none',
		}
	};

	return <HStack w='full' paddingX={'2'} paddingY={'1.5'} borderTop={'4px solid'} borderColor={{ base: 'neutral.700', _dark: 'neutral.300' }} gap={'2'} boxShadow={'sm'} borderBottomRadius={'sm'} bg={{ base: 'white', _dark: 'neutral.800' }}>
		{
			onToggleKeyboard &&
			<Button
				bg={isExpandedTicket ? expandedStyles.expanded.bg : expandedStyles.collapsed.bg}
				_hover={isExpandedTicket ? expandedStyles.expanded._hover : expandedStyles.collapsed._hover}
				_active={isExpandedTicket ? expandedStyles.expanded._active : expandedStyles.collapsed._active}
				border={isExpandedTicket ? expandedStyles.expanded.border : expandedStyles.collapsed.border}
				borderColor={isExpandedTicket ? expandedStyles.expanded.borderColor : expandedStyles.collapsed.borderColor}
				h='16'
				w='12'
				color={'white'}
				onClick={onToggleKeyboard}>
				<SvgIcon h='8' w='8' src={keypadIcon} />
			</Button>
		}
		<VStack flex={1} gap={'1'} lineHeight='normal'>
			<Total ticket={ticket} />
			<Discounts ticket={ticket} />
			<Prepayments ticket={ticket} />
		</VStack>
	</HStack>;
}